import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import './Radio.css';

/**
 * Radio Component
 * 
 * An accessible radio button component with label and validation states.
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.checked - Checked state (controlled)
 * @param {boolean} props.defaultChecked - Default checked state (uncontrolled)
 * @param {function} props.onChange - Change handler
 * @param {string} props.label - Label text
 * @param {boolean} props.disabled - Disabled state
 * @param {boolean} props.required - Required field
 * @param {string} props.size - Radio size: 'sm' | 'md' | 'lg'
 * @param {string} props.variant - Visual state: 'default' | 'error'
 * @param {string} props.name - Radio name attribute (for grouping)
 * @param {string} props.id - Radio id attribute
 * @param {string} props.value - Radio value attribute
 * @param {string} props.className - Additional CSS classes
 */
const Radio = forwardRef(({
  checked,
  defaultChecked,
  onChange,
  label,
  disabled = false,
  required = false,
  size = 'md',
  variant = 'default',
  name,
  id,
  value,
  className = '',
  ...props
}, ref) => {
  const containerClasses = [
    'radio-container',
    `radio-container--${size}`,
    `radio-container--${variant}`,
    disabled ? 'radio-container--disabled' : '',
    className
  ].filter(Boolean).join(' ');

  const radioClasses = [
    'radio',
    `radio--${size}`
  ].filter(Boolean).join(' ');

  return (
    <label className={containerClasses}>
      <input
        ref={ref}
        type="radio"
        checked={checked}
        defaultChecked={defaultChecked}
        onChange={onChange}
        disabled={disabled}
        required={required}
        name={name}
        id={id}
        value={value}
        className={radioClasses}
        aria-invalid={variant === 'error' ? 'true' : undefined}
        aria-required={required ? 'true' : undefined}
        {...props}
      />
      
      <span className="radio__indicator" aria-hidden="true">
        <span className="radio__dot"></span>
      </span>
      
      {label && (
        <span className="radio__label">
          {label}
          {required && <span className="radio__required" aria-label="required"> *</span>}
        </span>
      )}
    </label>
  );
});

Radio.displayName = 'Radio';

Radio.propTypes = {
  checked: PropTypes.bool,
  defaultChecked: PropTypes.bool,
  onChange: PropTypes.func,
  label: PropTypes.node,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  variant: PropTypes.oneOf(['default', 'error']),
  name: PropTypes.string.isRequired,
  id: PropTypes.string,
  value: PropTypes.string.isRequired,
  className: PropTypes.string,
};

export default Radio;
